import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';
import { OrderDetailEntity } from './order-detail.entity';
import { TripStopEntity } from './trip-stop.entity';

/**
 * Per-line-item delivery confirmation at a TripStop. One row per
 * order line delivered (or attempted) at the stop.
 *
 * The Order ↔ TripStop linkage is via `order.fk_trip_stop__id` (which
 * order goes to which stop). This entity sits at a finer grain — each
 * `order_detail` row delivered at the stop gets a `trip_stop_item`
 * row tracking the quantity ordered vs. the quantity actually handed
 * to the customer.
 *
 * Scope note (2026-05-13): the stop is customer-facing only. Driver
 * records `quantityDelivered` = units the customer accepted. Truck-
 * side exception tracking (damaged on the truck, missing, returned
 * units the driver brought back, etc.) was MOVED to the pre-trip /
 * post-trip inspection step, where each exception becomes a TRANSFER
 * `inventory_movement` from the truck into a destination bin
 * (morgue / returned / missing) on the home warehouse. See
 * `docs/claude/distribution-refactor/inspection-exceptions.md`.
 *
 * Removed in the same change: `quantityDamaged`, `quantityRejected`,
 * `creditAmount`, `creditApplied`. Migration:
 * `apps/config-api/src/migrations/instance/{ts}-DropTripStopItemDamageColumns.ts`.
 */
@Entity('trip_stop_item')
@Index('idx_trip_stop_item_stop', ['tripStopId'])
@Index('idx_trip_stop_item_order_detail', ['orderDetailId'])
export class TripStopItemEntity extends BaseEntity {
	@Column({ name: 'fk_trip_stop__id', type: 'int', unsigned: true })
	public tripStopId!: number;

	@Column({ name: 'fk_order_detail__id', type: 'int', unsigned: true })
	public orderDetailId!: number;

	@Column({ name: 'fk_item__id', type: 'int', unsigned: true })
	public itemId!: number;

	@Column({ name: 'quantityOrdered', type: 'int', unsigned: true })
	public quantityOrdered!: number;

	@Column({ name: 'quantityDelivered', type: 'int', unsigned: true })
	public quantityDelivered!: number;

	@Column({ name: 'reason', type: 'text', nullable: true })
	public reason?: string | null;

	@Column({ name: 'unitPrice', type: 'decimal', precision: 10, scale: 2 })
	public unitPrice!: number;

	@ManyToOne(() => TripStopEntity, (tripStop) => tripStop.items)
	@JoinColumn({ name: 'fk_trip_stop__id' })
	public fkTripStop?: TripStopEntity;

	@ManyToOne(() => OrderDetailEntity)
	@JoinColumn({ name: 'fk_order_detail__id' })
	public fkOrderDetail?: OrderDetailEntity;

	@ManyToOne(() => ItemEntity)
	@JoinColumn({ name: 'fk_item__id' })
	public fkItem?: ItemEntity;
}
